import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { INTENT_LABELS, PLATFORM_LABELS, timeAgo } from "@/lib/utils-buddy";
import { motion } from "framer-motion";
import { BookOpen, Trash2, Copy, Check, Search, Lock } from "lucide-react";

export default function LibraryPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const plan = user?.plan || "free";

  useEffect(() => { if (plan !== "free") fetchLibrary(); else setLoading(false); }, [plan]);

  const fetchLibrary = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/library");
      setItems(data.items);
    } catch {} finally { setLoading(false); }
  };

  const deleteItem = async (id) => {
    try { await API.delete(`/library/${id}`); setItems(items.filter(i => i.id !== id)); } catch {}
  };

  const copyText = (text, id) => {
    try { navigator.clipboard.writeText(text); } catch {}
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (plan === "free") {
    return (
      <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 pt-6 pb-16">
          <div className="text-center py-20">
            <Lock size={48} style={{ color: "var(--rolex-gold)" }} className="mx-auto mb-4" />
            <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>Saved Library</h2>
            <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>Save your best rewrites and access them anytime. Available on Pro plan and above.</p>
            <button onClick={() => window.location.href = "/pricing"} className="btn-primary py-2.5 px-6" data-testid="library-upgrade-btn">Upgrade to Pro</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-6 pb-16">
        <motion.div className="mb-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>Saved Library</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Your collection of perfect messages</p>
        </motion.div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl animate-shimmer" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <BookOpen size={40} style={{ color: "var(--surface-border-mid)" }} className="mx-auto mb-3" />
            <p style={{ color: "var(--text-muted)" }}>No saved messages yet. Save rewrites from your dashboard!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                className="p-5 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -2, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}
                data-testid={`library-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {item.intent && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>
                          {INTENT_LABELS[item.intent] || item.intent}
                        </span>
                      )}
                      {item.platform && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: "var(--surface-warm)", color: "var(--text-muted)" }}>
                          {PLATFORM_LABELS[item.platform] || item.platform}
                        </span>
                      )}
                      <span className="text-[10px]" style={{ color: "var(--text-light)" }}>{timeAgo(item.created_at)}</span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.content}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => copyText(item.content, item.id)}
                      className="p-1.5 rounded-md hover:bg-[var(--surface-warm)] transition-colors"
                      data-testid={`library-copy-${item.id}`}
                    >
                      {copiedId === item.id ? <Check size={14} style={{ color: "var(--success)" }} /> : <Copy size={14} style={{ color: "var(--text-light)" }} />}
                    </button>
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="p-1.5 rounded-md hover:bg-[var(--error-bg)] transition-colors"
                      data-testid={`library-delete-${item.id}`}
                    >
                      <Trash2 size={14} style={{ color: "var(--text-light)" }} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
